"use client";

import React from "react";
import { Shield, Activity, Lock, Globe, User, Mail, Phone, Building2, CircleAlert, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StartYourRecovery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="52012" id="access-the-network" ref={ref} className={`bg-background text-foreground relative py-24 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591123/img/topographic_map_dark.png)] mix-blend-multiply grayscale" />
      
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/20 text-primary text-xs font-mono tracking-widest uppercase">
              <Activity className="w-3 h-3" />
              Network Entry
            </div>
            <h2 className="text-4xl md:text-6xl font-bold font-serif tracking-tight text-foreground uppercase">
              Access the Network
            </h2>
            <p className="text-muted-foreground font-sans text-lg max-w-2xl mx-auto">
              Ready to enter the East? Connect with our strategic specialists to initiate your expansion architecture.
            </p>
          </div>

          <Card className="bg-white/40 backdrop-blur-xl border-white/50 shadow-xl overflow-hidden relative group rounded-lg">
            {}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-30" />
            
            <CardHeader className="border-b border-slate-200/50 pb-8">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-serif uppercase tracking-wide">Strategic Candidature</CardTitle>
                  <CardDescription className="font-sans">Direct transmission to the Geroi.eu tactical hub.</CardDescription>
                </div>
                <Shield className="w-8 h-8 text-primary/40" />
              </div>
            </CardHeader>

            <CardContent className="pt-8">
              <FormProvider formKey="access_network_form_heroes" formId="access_network_form_heroes" sectionName="start-recovery-section" defaultValues={{
              business_name: "",
              contact_name: "",
              email: "",
              phone: "",
              website: "",
              challenge: ""
            }} validationRules={{
              business_name: {
                required: true
              },
              contact_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              website: {
                pattern: "url"
              },
              challenge: {
                required: false
              }
            }} submitText="Connect with Us" successMessage="Candidature received. Our specialists are reviewing your profile for network compatibility.">
                <RecoveryForm />
              </FormProvider>
            </CardContent>
          </Card>

          <div className="mt-8 flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 text-xs text-muted-foreground font-sans italic">
            <div className="flex items-center gap-2">
              <Lock className="w-3 h-3 text-primary/60" />
              [Solo per progetti strutturati.]
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-3 h-3 text-primary/60" />
              [Budget di Espansione Annuale &gt;= €5.000]
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function RecoveryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 border border-primary/30 shadow-lg">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-serif uppercase text-foreground">Transmission Success</h3>
        <p className="text-muted-foreground font-sans max-w-md">{successMessage}</p>
        <Button variant="outline" className="mt-6 border-primary/20 hover:bg-primary/5 rounded-none font-sans" onClick={() => window.location.reload()}>
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="business_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-sans font-bold uppercase tracking-widest text-muted-foreground">Nome Azienda</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-primary/40" />
                  <Input placeholder="Enter company legal name" className="pl-9 bg-transparent border-0 border-b border-slate-200 focus:border-primary focus:ring-0 rounded-none transition-all font-sans" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="website" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-sans font-bold uppercase tracking-widest text-muted-foreground">Settore Industriale</FormLabel>
              <FormControl>
                <div className="relative">
                  <Globe className="absolute left-3 top-2.5 h-4 w-4 text-primary/40" />
                  <Input placeholder="e.g. Logistics, Energy, Digital" className="pl-9 bg-transparent border-0 border-b border-slate-200 focus:border-primary focus:ring-0 rounded-none transition-all font-sans" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="contact_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-sans font-bold uppercase tracking-widest text-muted-foreground">Mercato Target</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-primary/40" />
                  <Input placeholder="e.g. Eastern Europe, CIS" className="pl-9 bg-transparent border-0 border-b border-slate-200 focus:border-primary focus:ring-0 rounded-none transition-all font-sans" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-sans font-bold uppercase tracking-widest text-muted-foreground">Business Email</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-primary/40" />
                  <Input type="email" placeholder="corporate@example.com" className="pl-9 bg-transparent border-0 border-b border-slate-200 focus:border-primary focus:ring-0 rounded-none transition-all font-sans" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-sans font-bold uppercase tracking-widest text-muted-foreground">Contact Line</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-primary/40" />
                  <Input type="tel" placeholder="+1 (000) 000-0000" className="pl-9 bg-transparent border-0 border-b border-slate-200 focus:border-primary focus:ring-0 rounded-none transition-all font-sans" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <div className="md:col-span-2">
          <FormField control={control} name="challenge" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs font-sans font-bold uppercase tracking-widest text-muted-foreground">Project Overview</FormLabel>
                <FormControl>
                  <div className="relative">
                    <CircleAlert className="absolute left-3 top-2.5 h-4 w-4 text-primary/40" />
                    <Input placeholder="Briefly describe your expansion goals..." className="pl-9 bg-transparent border-0 border-b border-slate-200 focus:border-primary focus:ring-0 rounded-none transition-all font-sans" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-center gap-3 text-destructive">
          <CircleAlert className="w-5 h-5 shrink-0" />
          <p className="text-sm font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold font-serif uppercase tracking-widest bg-gradient-to-r from-[#D4AF37] via-[#F1D37E] to-[#B8860B] text-white shadow-lg hover:shadow-xl hover:brightness-110 transition-all duration-300 rounded-none border-0">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Transmitting...
          </> : "Connect with Us"}
      </Button>
    </Form>;
}